import { type AppContext } from "~/lib/context";

import { map } from "bluebird";
import { isNil, uniqBy } from "~/lib/lodash";
import { logWarn } from "~/lib/logger";

type JobPayloadParams = {
  companyId: number;
  externalId?: string | null;
  name: string | null;
};

export const buildJobPayload = (params: JobPayloadParams) => {
  const { companyId, externalId, name } = params;

  if (!name) {
    return null;
  }

  return {
    job: {
      connectOrCreate: {
        where: {
          companyId_externalId: {
            companyId,
            externalId: externalId ?? name,
          },
        },
        create: {
          name,
          externalId: externalId ?? name,
          autoMappingEnabled: true,
          company: {
            connect: { id: companyId },
          },
        },
      },
    },
  };
};

export type JobPayload = ReturnType<typeof buildJobPayload>;

export const getJobUpdatePayload = (params: JobPayload | undefined) => {
  if (isNil(params?.job)) {
    return null;
  }

  const {
    job: {
      connectOrCreate: { create },
    },
  } = params;

  return {
    jobs: true,
    companyId: create.company.connect.id,
    externalId: create.externalId,
    data: create,
  };
};

export const updateJobsPostSync = async (ctx: AppContext, jobs: NonNullable<JobUpdatePayload>[]) => {
  const uniqueJobs = uniqBy(jobs, (job) => job.companyId + job.externalId);

  return map(uniqueJobs, async (payload) => {
    const { companyId, externalId, data } = payload;

    const existingJob = await ctx.prisma.externalJob.findUnique({
      where: { companyId_externalId: { companyId, externalId } },
    });

    if (!existingJob) {
      logWarn(ctx, "[updateJobsPostSync] Job not found", {
        companyId,
        externalId,
      });
    }

    if (existingJob) {
      return ctx.prisma.externalJob.update({
        where: { id: existingJob.id },
        data,
      });
    }
  });
};

export type JobUpdatePayload = ReturnType<typeof getJobUpdatePayload>;
